<template>
  <ag-grid-vue
      @grid-ready="onGridReady"
      ref="agGridTable"
      :gridOptions="gridOptions"
      class="ag-theme-material w-100 ag-grid-table"
      :columnDefs="columnDefsTranslated"
      :defaultColDef="defaultColDef"
      :rowData="data"
      :rowSelection="rowSelection"
      :rowMultiSelectWithClick="true"
      :animateRows="true"
      :pagination="false"
      :suppressPaginationPanel="true"
      :paginationPageSize="50"
      :quickFilterText="search"
      :localeText="localeText"
      @rowClicked="onRowClicked"
      @cellClicked="onCellClicked"
      @selectionChanged="onSelectionChanged"
      @download="$emit('download', $event)"
      @online="$emit('online', $event)"
      @first-data-rendered="headerHeightSetter()"
      @column-resized="headerHeightSetter()"
      :sideBar="sideBarDef"
      :rowHeight="rowHeight"
      headerHeight="35"
      floatingFiltersHeight="35"
      :autoGroupColumnDef="autoGroupColumnDef"
      :treeData="treeData"
      :getDataPath="getDataPath"
      :groupDefaultExpanded="groupDefaultExpanded"
      :columnHoverHighlight="columnHoverHighlight"
      :auto-size="false"
      :tooltipShowDelay="tooltipShowDelay"
      :tooltipHideDelay="tooltipHideDelay"
  >
  </ag-grid-vue>
</template>

<script>
import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss'
import { AgGridVue } from 'ag-grid-vue'
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { RowGrouping } from '@ag-grid-enterprise/row-grouping';
import { SideBar } from '@ag-grid-enterprise/side-bar';
import { AG_GRID_LOCALE_ES } from './translations/locale.es';
import { AG_GRID_LOCALE_IT } from './translations/locale.it';
import { debounce } from 'lodash';

export default {
name: "t-grid",
  components: {
    AgGridVue,
  },
  props: {
    tooltipShowDelay: {
      type: Number,
      default: 0,
    },
    tooltipHideDelay: {
      type: Number,
      default: 9999999,
    },
    headerTextWrap: {
      type: Boolean,
      default: false,
    },
    showSidebar: {
      type: Boolean,
      default: true,
    },
    columnHoverHighlight: {
      type: Boolean,
      default: false,
    },
    columnDefs: {
      type: Array,
      required: true,
    },
    defaultColDef: {
      type: Object,
      default: () => {
        return {
          sortable: true,
          resizable: true,
          filter: 'agSetColumnFilter',
          floatingFilter: true,
        }
      }
    },
    gridOptions: {
      type: Object,
      default: () => {
        return {}
      }
    },
    autoSize: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Array,
      default: [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    search: {
      required: false,
      default: '',
    },
    treeData: {
      type: Boolean,
      default: false,
    },
    groupDefaultExpanded: {
      type: Number,
      default: 0,
    },
    getDataPath: {
      required: false,
    },
    autoGroupColumnDef: {
      type: Object,
      required: false,
      default: () => {
        return {
          // minWidth: 900
        };
      },
    },
    rowHeight: {
      type: Number,
      default: 30,
    },
    rowSelection: {
      type: String,
      default: 'none',
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      gridApi: null,
      modules: [
        RowGrouping,
        SideBar,
      ],
      localeText: null,
    };
  },
  watch: {
    loading(newValue) {
      const setLoadingState = () => {
        if (this.gridApi) {
          this.gridApi.setGridOption("loading", newValue);
        } else {
          setTimeout(setLoadingState, 500);
        }
      };

      setLoadingState();
    },
  },
  computed: {
    sideBarDef() {
      if (this.showSidebar) {
        return {
          toolPanels: [
            {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivotMode: true,
                },
              },
          ],
          defaultToolPanel: '',
        };
      }

      return false;
    },
    columnDefsTranslated () {
      let columnDefs = this.columnDefs;
      
      columnDefs.forEach((columnDef) => {
        if (columnDef.skipTranslation) {
          return true;
        }

        columnDef.headerName = this.$t(columnDef.headerName);

        if (columnDef.children) {
          columnDef.children.forEach((columnDefChildren) => {
            columnDefChildren.headerName = this.$t(columnDefChildren.headerName);
          });
        }
      });

      return columnDefs;
    }
  },
  mounted () {
    if (this.autoSize) {
      this.autoResizeGrid();
      this.autoResizeGridOnWindowResize();
    }

    this.$emit('ready', this.gridApi);
  },
  created () {
    this.setLocale();
    ModuleRegistry.registerModules([
      ClientSideRowModelModule
    ]);
  },
  methods: {
    onGridReady(params) {
      this.gridApi = params.api;
      this.$emit('update:gridApi', this.gridApi);
      this.$emit('gridReady', params)
    },
    autoResizeGridOnWindowResize() {
      window.addEventListener("resize", () => this.debouncedAutoResizeGrid());

      // add event listener for when the width of .ag-body-viewport changes
      const observer = new MutationObserver(() => this.debouncedAutoResizeGrid());
      const targetNode = document.querySelector('.ag-body-viewport');

      if (targetNode) {
        observer.observe(targetNode, {
          attributes: true,
          attributeFilter: ['style'],
        });
      } else {
        console.warn('Element with class .ag-body-viewport not found.');
      }
    },
    autoResizeGrid() {
      this.$nextTick(() => {
        if (this.gridApi) {
          this.gridApi.sizeColumnsToFit();
        } else {
          setTimeout(() => this.autoResizeGrid(), 500);
        }
      });
    },
    debouncedAutoResizeGrid: debounce(function() {
      this.autoResizeGrid();
    }, 100),
    headerHeightSetter() {
      if (this.hideHeader) {
        this.gridApi.setGridOption('headerHeight', 0)
      } else  if (this.headerTextWrap) {
        if (this.gridApi) {
          const padding = 20;
          const height = this.headerHeightGetter() + padding;
          this.gridApi.setGridOption('headerHeight', height)
          // this.gridApi.resetRowHeights();
        } else {
          setTimeout(() => this.autoResizeGrid(), 500);
        }
      }
    },
    headerHeightGetter() {
      const columnHeaderTexts = [
        ...document.querySelectorAll('.ag-header-cell-text'),
      ];
      const heights = columnHeaderTexts.map(
        headerText => headerText.clientHeight
      );
      const tallestHeaderTextHeight = Math.max(...heights);

      return tallestHeaderTextHeight;
    },
    setLocale() {
      if (this.$i18n.locale === 'it') {
        this.localeText = AG_GRID_LOCALE_IT;
      }

      if (this.$i18n.locale === 'es') {
        this.localeText = AG_GRID_LOCALE_ES;
      }
    },
    onRowClicked(params) {
      this.$emit('rowClicked', params);
    },
    onCellClicked(params) {
      this.$emit('cellClicked', params);
    },
    onSelectionChanged(params) {
      this.$emit('onSelectionChanged', params);
    }
  },
}
</script>


<style lang="scss">
@import '../../../assets/scss/custom/components/variables';

.header-text-wrap {
  .ag-header-cell-text {
    white-space: normal;
  }
}

.ag-theme-material {
  font-size: 12px;
  border-top: 1px solid $grey-light;
  margin: 1rem -1rem;
  padding: 1rem 1rem 0 1rem;

  .ag-header-row {
    color: #262626;
  }

  .ag-row-odd {
    background: #f8f8f8 !important;
  }

  .ag-checkbox-input-wrapper.ag-checked::after {
    color: #110D0D;
  }

  .ag-header-cell {
    padding: 0 12px;
  }

  .ag-cell {
    line-height: 25px;
    padding: 0 12px;
  }

  .ag-input-field {
    .ag-input-wrapper {
      .ag-input-field-input {
        border: 1px solid #c7c7c7;
        height: 24px;
        border-radius: 3px;
        padding: 2px 4px;
      }
    }
  }

  .ag-filter-filter {
    margin: 0 !important;
  }

  .ag-cell-wrapper > *:not(.ag-cell-value):not(.ag-group-value) {
    height: 100%;
  }

  .ag-ltr {
    .ag-floating-filter-button {
      margin-left: 6px;
    }
  }

  .ag-theme-material .ag-picker-field-wrapper {
    background-color: initial;
    border: none;
  }
}
</style>
